import { DateTime } from "luxon";

import { isMarketEarlyClose, isMarketHoliday, MARKET_CALENDAR_LAST_DAY } from './date-time';

/*
 * The NYSE clock: what market day it is and which session is running, read on New York time whatever the
 * viewer's own zone. Pure (no Vue); apps/web's useMarketClock() wraps it in one shared, ticking clock.
 * Holidays and early closes come from the tables in ./date-time, which end at MARKET_CALENDAR_LAST_DAY.
 */

/** The exchange's zone. Market days and sessions are always read on this clock, never the viewer's. */
export const MARKET_TZ: string = 'America/New_York';

/** An instant: a Luxon DateTime in any zone, a JS Date, or epoch milliseconds. */
export type MarketInstant = DateTime | Date | number;

/**
 * 'pre' 4:00–9:30 AM, 'regular' 9:30 AM–4:00 PM (1:00 PM on an early close), 'post' until 8:00 PM (5:00 PM on an
 * early close), all ET. Every other minute, and all of a weekend or holiday, is 'closed'.
 */
export type MarketSession = 'pre' | 'regular' | 'post' | 'closed';

export interface MarketSessionState {
    session: MarketSession;
    /** The instant's New York calendar day, 'yyyy-MM-dd' ('' for an invalid instant). */
    marketDay: string;
    isTradingDay: boolean;
    isEarlyClose: boolean;
    /** The regular session's open and close on marketDay, in MARKET_TZ; null when it is not a trading day. */
    opensAt: DateTime | null;
    closesAt: DateTime | null;
}

const DAY_FORMAT: string = 'yyyy-MM-dd';

// Minutes after midnight ET
const PRE_MARKET_OPEN: number = 4 * 60;
const REGULAR_OPEN: number = 9 * 60 + 30;
const REGULAR_CLOSE: number = 16 * 60;
const EARLY_CLOSE: number = 13 * 60;
const POST_MARKET_CLOSE: number = 20 * 60;
const EARLY_CLOSE_POST_MARKET_CLOSE: number = 17 * 60;

export function toMarketTime( instant: MarketInstant ): DateTime {
    if ( instant instanceof Date ) return DateTime.fromJSDate( instant, { zone: MARKET_TZ } );
    if ( typeof instant === 'number' ) return DateTime.fromMillis( instant, { zone: MARKET_TZ } );
    return instant.setZone( MARKET_TZ );
}

/** The New York calendar day of an instant, 'yyyy-MM-dd'; '' when the instant is invalid. */
export function marketDayOf( instant: MarketInstant ): string {
    return toMarketTime( instant ).toISODate() ?? '';
}

function parseMarketDay( day: string ): DateTime {
    return DateTime.fromFormat( day, DAY_FORMAT, { zone: MARKET_TZ } );
}

function requireMarketDay( day: string ): DateTime {
    const parsed: DateTime = parseMarketDay( day );
    // An invalid day would never become a trading day, so the walks below would not end
    if ( !parsed.isValid ) throw new RangeError( `Not a 'yyyy-MM-dd' market day: '${ day }'` );
    return parsed;
}

/** A weekday that is not an NYSE holiday. Only as good as the calendar tables: see isMarketCalendarCovered. */
export function isTradingDay( day: string ): boolean {
    const parsed: DateTime = parseMarketDay( day );
    return parsed.isValid && parsed.weekday <= 5 && !isMarketHoliday( day );
}

/** True while the holiday and early-close tables still cover `day`. */
export function isMarketCalendarCovered( day: string ): boolean {
    return day <= MARKET_CALENDAR_LAST_DAY;
}

/** Whole days from the instant's market day to the last day the calendar tables cover; negative once past it. */
export function marketCalendarDaysRemaining( instant: MarketInstant ): number {
    const today: DateTime = parseMarketDay( marketDayOf( instant ) );
    return Math.round( parseMarketDay( MARKET_CALENDAR_LAST_DAY ).diff( today, 'days' ).days );
}

function stepToTradingDay( day: string, step: 1 | -1 ): string {
    let cursor: DateTime = requireMarketDay( day );
    do {
        cursor = cursor.plus( { days: step } );
    } while ( !isTradingDay( cursor.toFormat( DAY_FORMAT ) ) );
    return cursor.toFormat( DAY_FORMAT );
}

/** The trading day strictly before `day`. Throws a RangeError for a malformed day. */
export function previousTradingDay( day: string ): string {
    return stepToTradingDay( day, -1 );
}

/** The trading day strictly after `day`. Throws a RangeError for a malformed day. */
export function nextTradingDay( day: string ): string {
    return stepToTradingDay( day, 1 );
}

/**
 * The instant's market day when it is a trading day, else the trading day before it (a weekend reads as Friday).
 * Throws a RangeError for an invalid instant.
 */
export function latestTradingDay( instant: MarketInstant ): string {
    const day: string = marketDayOf( instant );
    return isTradingDay( day ) ? day : previousTradingDay( day );
}

function atMinuteOfDay( day: DateTime, minuteOfDay: number ): DateTime {
    return day.set( { hour: Math.floor( minuteOfDay / 60 ), minute: minuteOfDay % 60, second: 0, millisecond: 0 } );
}

export function marketSessionAt( instant: MarketInstant ): MarketSessionState {
    const at: DateTime = toMarketTime( instant );
    const marketDay: string = at.toISODate() ?? '';

    if ( !isTradingDay( marketDay ) ) {
        return { session: 'closed', marketDay, isTradingDay: false, isEarlyClose: false, opensAt: null, closesAt: null };
    }

    const isEarlyClose: boolean = isMarketEarlyClose( marketDay );
    const regularClose: number = isEarlyClose ? EARLY_CLOSE : REGULAR_CLOSE;
    const postMarketClose: number = isEarlyClose ? EARLY_CLOSE_POST_MARKET_CLOSE : POST_MARKET_CLOSE;
    const minute: number = at.hour * 60 + at.minute;

    let session: MarketSession = 'closed';
    if ( minute >= PRE_MARKET_OPEN && minute < REGULAR_OPEN ) session = 'pre';
    else if ( minute >= REGULAR_OPEN && minute < regularClose ) session = 'regular';
    else if ( minute >= regularClose && minute < postMarketClose ) session = 'post';

    return {
        session,
        marketDay,
        isTradingDay: true,
        isEarlyClose,
        opensAt: atMinuteOfDay( at, REGULAR_OPEN ),
        closesAt: atMinuteOfDay( at, regularClose ),
    };
}

/**
 * The latest trading day whose regular session has closed, i.e. the newest day end-of-day data can exist for.
 * Replaces date-time's mostRecentValidMarketDate, which read the UTC day and never applied its closing-hour rule.
 * Throws a RangeError for an invalid instant.
 */
export function lastClosedTradingDay( instant: MarketInstant ): string {
    const state: MarketSessionState = marketSessionAt( instant );
    if ( state.closesAt && toMarketTime( instant ) >= state.closesAt ) return state.marketDay;
    return previousTradingDay( state.marketDay );
}
